import { useCallback, useMemo } from 'react';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { type ActionContext, type ToolbarPreferences } from '../types';

import {
  selectActionById,
  selectActionContext,
  selectIsVisible,
  selectPreferences,
  selectScrollContainerSelector,
  selectScrollControlEnabled,
  selectTotalBadgeCount,
} from './selectors';
import {
  clearBadgeCount,
  decrementBadgeCount,
  hideToolbar,
  incrementBadgeCount,
  setActionContext,
  setActionEnabled,
  setPreferences,
  setScrollContainerSelector,
  setScrollControlEnabled,
  showToolbar,
  toggleToolbar,
  updateActionContext,
  updateBadgeCount,
} from './slice';

export function useToolbar() {
  const dispatch = useAppDispatch();
  const isVisible = useAppSelector(selectIsVisible);
  const scrollControlEnabled = useAppSelector(selectScrollControlEnabled);
  const actionContext = useAppSelector(selectActionContext);
  const totalBadgeCount = useAppSelector(selectTotalBadgeCount);
  const preferences = useAppSelector(selectPreferences);
  const scrollContainerSelector = useAppSelector(selectScrollContainerSelector);
  const { toolbar } = useEventTracking();

  const show = useCallback(() => {
    toolbar.visibilityToggled({
      new_state: 'visible',
      trigger: 'manual_show',
    });
    dispatch(showToolbar());
  }, [dispatch, toolbar]);

  const hide = useCallback(() => {
    toolbar.visibilityToggled({
      new_state: 'hidden',
      trigger: 'manual_hide',
    });
    dispatch(hideToolbar());
  }, [dispatch, toolbar]);

  const toggle = useCallback(() => {
    toolbar.visibilityToggled({
      new_state: isVisible ? 'hidden' : 'visible',
      trigger: 'manual_toggle',
    });
    dispatch(toggleToolbar());
  }, [dispatch, isVisible, toolbar]);

  const setScrollControl = useCallback(
    (enabled: boolean) => {
      dispatch(setScrollControlEnabled(enabled));
    },
    [dispatch],
  );

  const setContext = useCallback(
    (context: ActionContext | undefined) => {
      dispatch(setActionContext(context));
    },
    [dispatch],
  );

  const updateContext = useCallback(
    (partialContext: Partial<ActionContext>) => {
      dispatch(updateActionContext(partialContext));
    },
    [dispatch],
  );

  const updatePreferences = useCallback(
    (prefs: Partial<ToolbarPreferences>) => {
      dispatch(setPreferences(prefs));
    },
    [dispatch],
  );

  const setScrollContainer = useCallback(
    (selector: string | undefined) => {
      dispatch(setScrollContainerSelector(selector));
    },
    [dispatch],
  );

  return {
    isVisible,
    scrollControlEnabled,
    actionContext,
    totalBadgeCount,
    preferences,
    scrollContainerSelector,
    show,
    hide,
    toggle,
    setScrollControl,
    setContext,
    updateContext,
    updatePreferences,
    setScrollContainer,
  };
}

export function useToolbarAction(actionId: string) {
  const dispatch = useAppDispatch();
  const action = useAppSelector(selectActionById(actionId));
  const actionContext = useAppSelector(selectActionContext);

  const setBadgeCount = useCallback(
    (count: number) => {
      dispatch(updateBadgeCount({ actionId, count }));
    },
    [dispatch, actionId],
  );

  const incrementBadge = useCallback(() => {
    dispatch(incrementBadgeCount(actionId));
  }, [dispatch, actionId]);

  const decrementBadge = useCallback(() => {
    dispatch(decrementBadgeCount(actionId));
  }, [dispatch, actionId]);

  const clearBadge = useCallback(() => {
    dispatch(clearBadgeCount(actionId));
  }, [dispatch, actionId]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      dispatch(setActionEnabled({ actionId, enabled }));
    },
    [dispatch, actionId],
  );

  return useMemo(
    () => ({
      ...action,
      actionContext,
      setBadgeCount,
      incrementBadge,
      decrementBadge,
      clearBadge,
      setEnabled,
    }),
    [action, actionContext, setBadgeCount, incrementBadge, decrementBadge, clearBadge, setEnabled],
  );
}

export function useToolbarVisibility() {
  const dispatch = useAppDispatch();
  const isVisible = useAppSelector(selectIsVisible);
  const scrollControlEnabled = useAppSelector(selectScrollControlEnabled);
  const { toolbar } = useEventTracking();

  const show = useCallback(() => {
    toolbar.visibilityToggled({
      new_state: 'visible',
      trigger: 'manual_show',
    });
    dispatch(showToolbar());
  }, [dispatch, toolbar]);

  const hide = useCallback(() => {
    toolbar.visibilityToggled({
      new_state: 'hidden',
      trigger: 'manual_hide',
    });
    dispatch(hideToolbar());
  }, [dispatch, toolbar]);

  const toggle = useCallback(() => {
    toolbar.visibilityToggled({
      new_state: isVisible ? 'hidden' : 'visible',
      trigger: 'manual_toggle',
    });
    dispatch(toggleToolbar());
  }, [dispatch, isVisible, toolbar]);

  const setScrollControl = useCallback(
    (enabled: boolean) => {
      dispatch(setScrollControlEnabled(enabled));
    },
    [dispatch],
  );

  return {
    isVisible,
    scrollControlEnabled,
    show,
    hide,
    toggle,
    setScrollControl,
  };
}
